import React, {
  Dispatch,
  Fragment,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  useTable,
  useSortBy,
  useFilters,
  useRowSelect,
  useGroupBy,
  useExpanded,
} from 'react-table';
import { useVirtual } from 'react-virtual';

import RMCheckBox from 'components/customComponents/Controls/tableSelectionCheckBox';
import { ReactComponent as ArrowDown } from 'Icons/ArrowDown.svg';
import { ReactComponent as ArrowRight } from 'Icons/ArrowRight.svg';
import { ActionButtons, EditableCell } from 'components/table';
import { isEmpty } from 'lodash';
import RMSvgIcon from '../CommonComponents/RMSvgIcon';
import { KeyboardKeys } from 'constants/types';
import { useScrollToSelectedRow } from './hooks/useScrollToSelectedRow';
import { FmlxCheckBox, FmlxTooltip } from 'fmlx-common-ui';
// import Header from 'components/header/Header';
/**
 * TODO: This Component needs to improve further.
 */

export interface RowEditProps {
  currentEditingRowId?: number;
  setCurrentEditingRowId?: React.Dispatch<React.SetStateAction<number>>;
  handleOnCellContentEditComplete?: (updatedRowContent) => void;
  showActionButtons?: boolean;
  showEditButton?: boolean;
  rowSaveHandler?: (...e) => void;
  rowDiscardHandler?: (...e) => void;
}

export enum RowInfoType {
  Error,
  Warning,
  Info,
}

export type RowExtraInfo = {
  [key: number]: {
    type: RowInfoType;
    msg: string;
  };
};
export interface TableProps {
  columns: any;
  onCheckboxSelectionChange?: any;
  data: any;
  onRowClicked?: any;
  onRowDoubleClicked?: any;
  onRowRightClicked?: (row: any, event: React.MouseEvent) => void;
  uniqueColumnName?: any;
  uniqueValue?: any;
  groupingColumns?: Array<string>;
  hiddenColumns?: Array<string>;
  selectedIds?: number[];
  mainContainerStyle?: React.CSSProperties;
  enableMultiSelection?: boolean;
  selectRowOnClick?: boolean;
  tableClass?: string;
  rowEditProps?: RowEditProps;
  rowExtraInfo?: RowExtraInfo;
  headerClassName?: string;
  cellClassName?: string;
  setFilteredRowsIndex?: Dispatch<SetStateAction<number[]>>;
  groupingSeparator?: JSX.Element;
  rowClassNamePostfix?: string;
  scrollToSelected?: boolean;
  tooltipColumns?: string[];
  disableUnsort?: boolean;
  showToolTipOnHover?: boolean;
}

const defaultEditingCell = {
  Cell: EditableCell,
};

const defaultRowEditProps: RowEditProps = {
  currentEditingRowId: null,
  setCurrentEditingRowId: null,
  handleOnCellContentEditComplete: null,
  showActionButtons: false,
  showEditButton: false,
  rowSaveHandler: null,
  rowDiscardHandler: null,
};

const RMTable = (props: TableProps) => {
  const {
    selectedIds = [],
    columns,
    onCheckboxSelectionChange = null,
    data,
    onRowClicked = (e) => {},
    onRowDoubleClicked = (e) => {},
    onRowRightClicked = null,
    uniqueColumnName = '',
    uniqueValue = null,
    groupingColumns = null,
    hiddenColumns,
    mainContainerStyle = null,
    enableMultiSelection = true,
    selectRowOnClick = false,
    tableClass = '',
    rowEditProps,
    rowExtraInfo,
    headerClassName = '',
    cellClassName = '',
    setFilteredRowsIndex,
    groupingSeparator = null,
    rowClassNamePostfix = '',
    scrollToSelected = false,
    tooltipColumns = null,
    disableUnsort = false,
    showToolTipOnHover = false,
  } = props;

  const {
    currentEditingRowId = null,
    setCurrentEditingRowId,
    handleOnCellContentEditComplete = null,
    showActionButtons = true,
    showEditButton = showActionButtons ? true : false,
    rowSaveHandler,
    rowDiscardHandler,
  } = rowEditProps || defaultRowEditProps;

  if (showEditButton && !showActionButtons) {
    throw new Error('Action buttons must be enabled to show edit button.');
  }

  if (showActionButtons && !setCurrentEditingRowId) {
    throw new Error('Setter for currentEditingRowId must be provided.');
  }

  const [rowEditingContent, setRowEditingContent] = useState({});
  const [isTableInFocus, setIsTableInFocus] = useState(false);

  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  function handleCheckBoxSelection(rowId, replace = false) {
    let selectedRowList = replace ? [] : [...selectedIdsRef.current];
    if (selectedRowList.includes(rowId)) {
      selectedRowList = selectedRowList.filter((id) => id !== rowId);
    } else {
      if (enableMultiSelection === false || replace) {
        selectedRowList = [];
      }
      selectedRowList.push(rowId);
    }
    if (onCheckboxSelectionChange) {
      onCheckboxSelectionChange(selectedRowList);
    }
  }

  const containerRef = useRef<HTMLDivElement | null>(null);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGroupBy,
    toggleAllRowsExpanded,
  } = useTable(
    {
      columns,
      data,
      currentEditingRowId,
      rowEditingContent,
      setRowEditingContent,
      defaultColumn: defaultEditingCell,
      initialState: {
        selectedRowIds: selectedIdsRef.current,
        ...(hiddenColumns?.length && {
          hiddenColumns: hiddenColumns,
        }),
      },
      autoResetFilters: false,
      autoResetSortBy: false,
      disableSortRemove: disableUnsort,
    },
    useFilters,
    useGroupBy,
    useSortBy,
    useExpanded,
    useRowSelect,
    (hooks: any) => {
      hooks.visibleColumns.push((columns: any) => {
        const checkBoxColumn = {
          id: 'selection',
          Header: (event: any) => {
            const { onChange, ...rest } = event.getToggleAllRowsSelectedProps();
            const allIds = Array.from(Array(event.data.length).keys());
            const _onChange = (e) => {
              onChange(e);

              if (event.data.length == selectedIdsRef.current.length) {
                onCheckboxSelectionChange([]);
              } else {
                onCheckboxSelectionChange(allIds);
              }
            };

            return enableMultiSelection === true ? (
              <RMCheckBox
                id="rmtable-header-checkbox"
                data-testid="rmtable-header-checkbox"
                event={event}
                onChange={_onChange}
                {...rest}
                style={{ margin: '6px 6px' }}
                indeterminate={
                  selectedIdsRef.current?.length > 0 &&
                  allIds.length > selectedIdsRef.current?.length
                }
                checked={
                  selectedIdsRef.current?.length > 0 &&
                  allIds.length === selectedIdsRef.current.length
                }
              />
            ) : (
              <></>
            );
          },
          Cell: (event: any) => {
            const { onChange, ...rest } = event.row.getToggleRowSelectedProps();
            const _onChange = (e) => {
              onChange(e);
              handleCheckBoxSelection(event.row.index);
            };

            return (
              <RMCheckBox
                id={`rmtable-row-checkbox-${event.row.index}`}
                data-testid={`rmtable-row-checkbox-${event.row.index}`}
                event={event}
                onChange={_onChange}
                {...rest}
                indeterminate={false}
                checked={selectedIdsRef.current.includes(event.row.index)}
              />
            );
          },
        };

        const actionButtonsColumn = {
          id: 'editActions',
          Header: 'Action',
          Cell: (event: any) => {
            return (
              <ActionButtons
                cellEvent={event}
                currentEditingRowId={currentEditingRowId}
                setCurrentEditingRowId={setCurrentEditingRowId}
                rowDiscardHandler={rowDiscardHandler}
                rowSaveHandler={rowSaveHandler}
                showEditButton={showEditButton}
              />
            );
          },
        };
        let allColumns = [onCheckboxSelectionChange ? checkBoxColumn : {}, ...columns];
        if (showActionButtons) {
          allColumns = [...allColumns, actionButtonsColumn];
        }
        if (!onCheckboxSelectionChange) {
          allColumns.shift();
        }
        return allColumns;
      });
    },
  );

  const {
    virtualItems: virtualRows,
    totalSize,
    scrollToIndex,
  } = useVirtual({
    parentRef: containerRef,
    size: rows.length,
    overscan: 20,
    estimateSize: useCallback(() => 40, []),
  });

  useScrollToSelectedRow({
    selectedIds: selectedIdsRef.current,
    scrollToSelected,
    scrollToIndex,
    containerRef,
  });

  /**
   * Padding for top & bottom is there to trigger scroll effect to load more data.
   */
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;

  useEffect(() => {
    if (groupingColumns) {
      setGroupBy(groupingColumns);
    }
  }, [groupingColumns, setGroupBy]);

  useEffect(() => {
    if (groupingColumns) {
      toggleAllRowsExpanded(true);
    }
  }, [headerGroups]);

  useEffect(() => {
    if (
      !isEmpty(rowEditingContent) &&
      currentEditingRowId != null &&
      handleOnCellContentEditComplete
    ) {
      handleOnCellContentEditComplete(rowEditingContent);
    }
  }, [rowEditingContent]);

  /**
   * This is a temporary fix
   * we need to upgrade the react-table to v8, in order to
   * achieve this functionality
   */
  useEffect(() => {
    if (setFilteredRowsIndex) {
      setFilteredRowsIndex(rows.map((row) => row.index));
    }
  }, [rows]);

  useEffect(() => {
    if (currentEditingRowId !== null) {
      setRowEditingContent(data[currentEditingRowId]);
    } else if (!isEmpty(rowEditingContent)) {
      setRowEditingContent({});
    }
  }, [currentEditingRowId]);

  const handleUserKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (!isTableInFocus) {
        return;
      }

      if (event.key === KeyboardKeys.ArrowDown || event.key === KeyboardKeys.ArrowUp) {
        const lastSelectedIndex = selectedIdsRef.current.length
          ? selectedIdsRef.current[selectedIdsRef.current.length - 1]
          : 0;
        const newIndex =
          event.key === KeyboardKeys.ArrowDown
            ? Math.min(lastSelectedIndex + 1, data.length - 1)
            : Math.max(lastSelectedIndex - 1, 0);

        if (onCheckboxSelectionChange) {
          handleCheckBoxSelection(newIndex, true);
        } else if (onRowClicked) {
          onRowClicked(rows[newIndex]);
        }
        if (onCheckboxSelectionChange || onRowClicked) {
          scrollToIndex(newIndex, {
            align: 'center',
          });
        }
      }
    },
    [
      rows,
      selectedIdsRef.current,
      handleCheckBoxSelection,
      onCheckboxSelectionChange,
      onRowClicked,
      isTableInFocus,
    ],
  );

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    window.addEventListener('keydown', handleUserKeyPress, { signal });
    return () => {
      abortController.abort();
    };
  }, [handleUserKeyPress]);

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const handleClickOutside = (event) => {
      const current = containerRef.current;
      if (current) {
        setIsTableInFocus(current?.contains(event.target));
      }
    };
    document.addEventListener('mousedown', handleClickOutside, { signal });
    return () => {
      abortController.abort();
    };
  }, [containerRef]);

  const handleOnRowClick = (row) => {
    if (selectRowOnClick) {
      handleCheckBoxSelection(row.index, true);
    } else if (onRowClicked) {
      onRowClicked(row);
    }
  };

  const getRowBackgroundColor = (row) => {
    if (rowExtraInfo && rowExtraInfo[row.index]) {
      return selectedIdsRef.current.includes(row.index)
        ? 'rgba(133,186,255,1)'
        : '#FF7171';
    }
    if (selectedIdsRef.current.includes(row.index)) {
      if (
        enableMultiSelection &&
        selectedIdsRef.current[selectedIdsRef.current.length - 1] === row.index
      ) {
        return 'rgba(92, 162, 254, 1)';
      }
      return 'rgba(133,186,255,1)';
    }
    return null;
  };

  const cellRenderer = (row, cell, i) => {
    const cellValue = cell.row.original[cell.column.id];

    const renderBooleanOrDefaultCell = () => {
      if (typeof cellValue === 'boolean') {
        return <FmlxCheckBox checked={cellValue} size="sm" />;
      }

      return cell.value ? cell.render('Cell') : '';
    };

    if (rowExtraInfo && rowExtraInfo[row.index] && i === 0) {
      return (
        <div
          id="rmtable-cellrender-wrapper"
          data-testid="rmtable-cellrender-wrapper"
          title={rowExtraInfo[row.index].msg}
          className="flex flex-row items-center gap-1"
        >
          <div
            id="rmtable-cellrender-error-icon"
            data-testid="rmtable-cellrender-error-icon"
            className="grow-0"
          >
            <RMSvgIcon
              id="rmtable-cellrender-svg-error-icon"
              name="Error"
              fill="#7b0000"
              className="w-4 h-4"
            />
          </div>
          {renderBooleanOrDefaultCell()}
        </div>
      );
    } else {
      cell.value = cellValue;
      return tooltipColumns &&
        cell.column?.id &&
        tooltipColumns.includes(cell.column?.id) ? (
        <FmlxTooltip
          id={`rmtable-cellrender-tooltip-${row.Index}`}
          data-testid={`rmtable-cellrender-tooltip-${row.Index}`}
          title={cell.value}
        >
          <div
            id={`rmtable-cellrender-tooltip-content-${row.Index}`}
            data-testid={`rmtable-cellrender-tooltip-content-${row.Index}`}
            className={cell.column?.className ?? ''}
          >
            {renderBooleanOrDefaultCell()}
          </div>
        </FmlxTooltip>
      ) : typeof cellValue === 'boolean' ? (
        <FmlxCheckBox checked={cellValue} size="sm" />
      ) : (
        cell.render('Cell')
      );
    }
  };

  /**
   * Apply max-width if selection checkbox is used.(in first column.)
   */
  const handleCheckboxColumnWidth = onCheckboxSelectionChange
    ? 'checkbox-width-handler'
    : '';

  return (
    <div
      id="rmtable-main-container"
      data-testid="rmtable-main-container"
      className={`rmtable-main-container ${handleCheckboxColumnWidth}`}
      style={mainContainerStyle}
      ref={containerRef}
    >
      <table
        id={`rmtable-content`}
        data-testid={`rmtable-content`}
        className={tableClass}
        {...getTableProps()}
      >
        <thead
          id={`rmtable-content-header`}
          data-testid={`rmtable-content-header`}
          className="rmtable-thead"
        >
          {headerGroups.map((headerGroup, i) => (
            <tr
              id={`rmtable-header-row-${i}`}
              data-testid={`rmtable-header-row-${i}`}
              className={`rmtable-tr${rowClassNamePostfix}`}
              {...headerGroup.getHeaderGroupProps()}
              key={`rmtable-header-${i}`}
            >
              {headerGroup.headers.map((column, j) => {
                if (column?.show != false) {
                  return (
                    <Fragment key={j}>
                      {j > 0 && headerGroup.headers.length > 2 && (
                        <div
                          id={`rmtable-header-separator-${i}-${j}`}
                          data-testid={`rmtable-header-separator-${i}-${j}`}
                          className="th-sepration"
                        ></div>
                      )}
                      <th
                        id={`rmtable-header-cell-${i}-${column.id}`}
                        data-testid={`rmtable-header-cell-${i}-${column.id}`}
                        className={`rmtable-th${rowClassNamePostfix} ${
                          column.className ? column.className : headerClassName
                        }`}
                        {...column.getHeaderProps()}
                      >
                        {column.Filter
                          ? column.render('Filter')
                          : column.render('Header')}
                      </th>
                    </Fragment>
                  );
                }
              })}
            </tr>
          ))}
        </thead>
        <tbody
          id={`rmtable-content-body`}
          data-testid={`rmtable-content-body`}
          {...getTableBodyProps()}
        >
          {paddingTop > 0 && (
            <tr id="rmtable-padding-top" data-testid="rmtable-padding-top">
              <td style={{ height: `${paddingTop}px` }} />
            </tr>
          )}
          {virtualRows.map((virtualRow) => {
            const row = rows[virtualRow.index];
            if (!row) {
              return null;
            }
            prepareRow(row);
            return (
              <>
                {row.canExpand ? (
                  <tr
                    id={`rmtable-group-row-${row.index}`}
                    data-testid={`rmtable-group-row-${row.index}`}
                    className="rmtable-group-heading"
                    {...row.getToggleRowExpandedProps({
                      style: {
                        paddingLeft: `${row.depth * 2}rem`,
                        height: '40px',
                      },
                    })}
                  >
                    <td
                      id={`rmtable-group-cell-${row.index}`}
                      data-testid={`rmtable-group-cell-${row.index}`}
                      style={{
                        width: '100%',
                      }}
                    >
                      <div
                        id={`rmtable-group-content-${row.index}`}
                        data-testid={`rmtable-group-content-${row.index}`}
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}
                      >
                        {row.isExpanded ? (
                          <ArrowDown
                            id="rmtable-chevron-icon-down"
                            data-testid="rmtable-chevron-icon-down"
                          />
                        ) : (
                          <ArrowRight
                            id="rmtable-chevron-icon-right"
                            data-testid="rmtable-chevron-icon-right"
                          />
                        )}
                        {`${row.groupByVal}s (${row.subRows?.length})`}
                        {groupingSeparator ? (
                          groupingSeparator
                        ) : (
                          <div
                            id={`rmtable-group-line-${row.index}`}
                            data-testid={`rmtable-group-line-${row.index}`}
                            className="rmtable-group-heading-line"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr
                    id={`rmtable-body-row-${row.index}`}
                    data-testid={`rmtable-body-row-${row.index}`}
                    className={`rmtable-tr${rowClassNamePostfix} ${
                      selectedIdsRef.current.includes(row.index) ? 'selected-row' : ''
                    }`}
                    onDoubleClick={() => onRowDoubleClicked(row)}
                    {...row.getRowProps({
                      onClick: () => handleOnRowClick(row),
                      onContextMenu: (event: React.MouseEvent) => {
                        if (onRowRightClicked) {
                          event.preventDefault();
                          onRowRightClicked(row, event);
                        }
                      },
                    })}
                    style={{
                      background:
                        row.values[uniqueColumnName] === uniqueValue
                          ? 'rgb(248,151,35)'
                          : '',
                      cursor: selectRowOnClick ? 'pointer' : 'default',
                      height: '40px',

                      backgroundColor: getRowBackgroundColor(row),
                    }}
                  >
                    {row.cells.map((cell, i) => {
                      return (
                        <td
                          id={`rmtable-body-cell-${row.index}-${cell.column.id}`}
                          data-testid={`rmtable-body-cell-${row.index}-${cell.column.id}`}
                          key={`rm-table-${i}`}
                          {...(typeof cell.value === 'string' && showToolTipOnHover
                            ? { title: cell.value }
                            : {})}
                          className={
                            cell.column?.className
                              ? cell.column?.className
                              : cellClassName
                          }
                          {...cell.getCellProps()}
                        >
                          {cellRenderer(row, cell, i)}
                        </td>
                      );
                    })}
                  </tr>
                )}
              </>
            );
          })}
          {paddingBottom > 0 && (
            <tr id="rmtable-padding-bottom" data-testid="rmtable-padding-bottom">
              <td style={{ height: `${paddingBottom}px` }}></td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default RMTable;
